/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 *
 * Component: Filter
 * Date: 6/8/2020
 *
 */

import React, { forwardRef, useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';

import {
  MultiSelectFilter,
  MultiSelectTree,
  SlideFilter,
  DateSelectionFilter,
  SingleSelectFilter,
  MultipleFilter,
  SkeletonLoader,
} from 'components';
// import MoreFilters from '../MoreFilters';
import {
  // Input,
  Button,
  // Icon,
} from 'components/common';

import styles from './style.module.css';

const Filter = forwardRef(
  (
    {
      filters,
      onSearchClick,
      onInputChange,
      resultsLoading,
      filtersLoading,
      extraButtons,
      filterLimit = 3,
      defaultSearchString,
      clearSearch = null,
      moreFilter,
      placeholder = '',
      selectedValues = [],
      conditions = {},
      skeletonLoaders,
      searchButtonText,
      onlyMorefilters,
      ...rest
    }: any,
    ref: any,
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    const [selectedFiltersState, setSelectedFilters] = useState(selectedValues);
    useEffect(() => {
      setSelectedFilters(selectedValues);
    }, [selectedValues]);
    /**
     * Main function to get filters depending on it's type and provide right props
     *
     * @param -> {Object} filter
     * @return -> Filter component
     *
     */
    const getFiltersByType = (filter: any): any => {
      const filterValues = filters[filter.field];
      const selectedValue = selectedFiltersState.find(
        (selected: { field: any }) =>
          selected.field === filter.field ||
          selected.field === filter.customField,
      );
      let filterSelectedValues = [];
      if (filter.type === 'multiple' && selectedValue?.clauses) {
        filterSelectedValues = selectedValue?.clauses;
      } else if (selectedValue?.value) {
        filterSelectedValues = selectedValue?.value;
      }
      const appliedConditionProps = selectedValue?.condition || 'in';
      switch (filter.type) {
        case 'singleSelect':
          return (
            <SingleSelectFilter
              key={filter.field}
              {...rest}
              filter={filter}
              onFilterApplied={onFilterApplied}
              filterValues={filterValues}
              selectedValues={filterSelectedValues}
            />
          );
        case 'multiSelect':
          return (
            <MultiSelectFilter
              key={filter.field}
              {...rest}
              filter={filter}
              onFilterApplied={onFilterApplied}
              filterValues={filterValues}
              selectedValues={filterSelectedValues}
              showConditionOption
              condition={appliedConditionProps}
            />
          );
        case 'slider': {
          const slidetype = filter.subtype || 'duration';
          return (
            <SlideFilter
              key={filter.field}
              {...rest}
              filter={filter}
              onFilterApplied={onFilterApplied}
              min={filterValues.min}
              max={filterValues.max}
              type={slidetype}
              selectedValues={filterSelectedValues}
            />
          );
        }
        case 'date':
          return (
            <DateSelectionFilter
              key={filter.field}
              {...rest}
              filter={filter}
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              onFilterApplied={onFilterApplied}
              selectedValues={filterSelectedValues}
            />
          );
        case 'multiple':
          return (
            <MultipleFilter
              setShowMoreFilter={() => {}}
              key={filter.field}
              {...rest}
              filter={filter}
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              onFilterApplied={onFilterApplied}
              filterValues={filterValues}
              selectedValues={filterSelectedValues}
            />
          );
        case 'multiTree':
          return (
            <MultiSelectTree
              setShowMoreFilter={() => {}}
              key={filter.field}
              {...rest}
              filter={filter}
              buttonName={filter.name || ''}
              filterValues={filterValues}
              onFilterApplied={onFilterApplied}
              selectedValues={filterSelectedValues}
            />
          );
        default:
          return <></>;
      }
    };
    /**
     * This function is to set filters in reducer in a format that they can be directly sent to API request
     *
     * @param -> newAppliedFilterValues is {Object}
     * @return -> returns nothing just calls a function to set filters in redux store
     *
     */
    const onNewFiltersApplied = (
      newAppliedFilterValues: { [key: string]: any } = {},
      newFilterCondition: any = {},
    ) => {
      const appliedFilterKeys = newAppliedFilterValues
        ? Object.keys(newAppliedFilterValues)
        : [];
      let newFilters = selectedFiltersState?.filter(
        (filter: { field: string }) =>
          !appliedFilterKeys.includes(filter.field),
      );
      appliedFilterKeys.forEach((key) => {
        const newSelectedValues = newAppliedFilterValues[key];
        // eslint-disable-next-line no-underscore-dangle
        let typeOfFilter = filters.displayFilters.find(
          (f: { field: string; customField: string }) =>
            f.field === key || f.customField === key,
        );
        typeOfFilter = typeOfFilter ? typeOfFilter.type : '';
        if (newSelectedValues?.length) {
          if (typeOfFilter === 'multiSelect') {
            newFilters.push({
              field: key,
              condition: newFilterCondition[key] || 'in',
              value: newSelectedValues,
              type: 'multiSelect',
            });
          } else if (typeOfFilter === 'multiple') {
            newFilters.push({
              field: key,
              condition: 'multiple',
              clauses: newSelectedValues,
              type: 'multiple',
            });
          } else if (typeOfFilter === 'singleSelect') {
            newFilters.push({
              field: key,
              condition: 'in',
              value: [newSelectedValues],
              type: 'singleSelect',
            });
          } else if (typeOfFilter === 'upload') {
            newFilters.push({
              field: key,
              condition: 'in',
              value: newSelectedValues,
              type: 'upload',
            });
          } else if (typeOfFilter === 'slider') {
            const min = filters && filters[key].min;
            const max = filters && filters[key].max;
            // eslint-disable-next-line sonarjs/no-collapsible-if
            if (min || max) {
              if (
                Math.floor(min) !== newSelectedValues[0] ||
                Math.ceil(max) !== newSelectedValues[1]
              ) {
                newFilters.push({
                  field: key,
                  condition: 'between',
                  value: newSelectedValues,
                  type: 'slider',
                });
              }
            }
          } else if (typeOfFilter === 'date') {
            const dateFilter = filters.displayFilters.find(
              (f: { field: string; customField: string }) =>
                f.field === key || f.customField === key,
            );
            newFilters = newFilters.filter((f: { field: any }) => {
              // eslint-disable-next-line no-underscore-dangle
              if (
                f.field === dateFilter.field ||
                f.field === dateFilter.customField
              ) {
                return false;
              }
              return true;
            });
            let filterDates = [];
            if (_.isArray(newSelectedValues) && newSelectedValues.length > 1) {
              // [ start date, end date ] array is expected here
              filterDates = [];
              const [startDate, endDate] = newSelectedValues;
              filterDates.push(
                moment(startDate).startOf('d').format('YYYY-MM-DDTHH:mm:ss'),
              );
              filterDates.push(
                moment(endDate).endOf('d').format('YYYY-MM-DDTHH:mm:ss'),
              );

              newFilters.push({
                field: key,
                condition: 'between',
                value: filterDates,
                type: 'date',
              });
            } else if (_.isString(newSelectedValues)) {
              // '7', '90', etc like values are expected here
              newFilters.push({
                field: key,
                condition: 'equals',
                value: newSelectedValues,
                type: 'date',
              });
            } else {
              console.error('Unexpected value of date filter');
            }
          } else if (typeOfFilter === 'multiTree') {
            newFilters.push({
              field: key,
              condition: 'equals',
              value: newSelectedValues,
              type: 'multiTree',
            });
          } else {
            newFilters.push({
              field: key,
              condition: 'equals',
              value: newSelectedValues,
            });
          }
        }
      });
      setSelectedFilters(newFilters);
      if (onlyMorefilters) {
        onSearchClick(newFilters);
      }
    };
    const onFilterApplied = (
      key: string | number,
      selectedValuesArgs: any,
      conditionArgs: any,
    ) => {
      const selectedFilter: { [key: string]: any } = {};
      selectedFilter[key] = selectedValuesArgs;
      onNewFiltersApplied(selectedFilter, { [key]: conditionArgs });
    };
    const onMoreFilterApplied = (moreFilters: any[]) => {
      const inObjectFormat: any = {};
      const inObjectCondition: any = {};
      moreFilters.forEach(
        (moreFilterArgs: {
          condition: string;
          field: string | number;
          clauses: any;
          value: any;
        }) => {
          if (moreFilterArgs.condition === 'multiple') {
            inObjectFormat[moreFilterArgs.field] = moreFilterArgs.clauses;
          } else {
            inObjectFormat[moreFilterArgs.field] = moreFilterArgs.value;
          }
          inObjectCondition[moreFilterArgs.field] = moreFilterArgs.condition;
        },
      );
      onNewFiltersApplied(inObjectFormat, inObjectCondition);
      // if (boolean) {
      //   handleNewFilters(selectedFiltersState);
      // }
    };
    return (
      <div id={`${styles.container}`} className={styles.container}>
        <SkeletonLoader type={skeletonLoaders}>
          {!_.isEmpty(filters) && (
            <>
              <div
                className={`${styles.filterDivContainer} ${
                  onlyMorefilters ? styles.onlyMoreFilters : ''
                }`}
              >
                <div className={styles.filterDiv}>
                  {filters.displayFilters.map(
                    (displayfilter: any, index: number) => {
                      if (index < filterLimit)
                        return getFiltersByType(displayfilter);
                      return null;
                    },
                  )}
                  {/* {getMoreFilters()} */}
                </div>
                {!ref && !onlyMorefilters && (
                  <div className={styles.reviewScorecardButtons}>
                    {onSearchClick && (
                      <Button
                        type="primary"
                        loading={resultsLoading}
                        disabled={selectedFiltersState.length < 2}
                        onClick={() => onSearchClick(selectedFiltersState)}
                        text={searchButtonText || 'Search'}
                      />
                    )}
                    {clearSearch && (
                      <Button
                        width={50}
                        className="clear-search"
                        onClick={() => {
                          setSelectedFilters([]);
                          clearSearch();
                        }}
                        text="Clear"
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </SkeletonLoader>
      </div>
    );
  },
);
Filter.displayName = 'Filter';

export default Filter;
